"use client";

import * as React from "react";
import Image, { type ImageLoader } from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { urlFor } from "@/sanity/lib/image";
import CtaButton from "@/components/ui/CtaButton";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import Underline from "./ui/underline";
import TextLinkButton from "@/components/ui/TextLinkButton";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";

type PreparedBlogPost = {
    url: string;
    title: string;
    imageSanity?: SanityImageSource;
    imageAlt?: string;
    blurDataURL?: string;
    image?: { src: string; alt?: string };
};

type BaseProps = {
    breadcrumbs?: BreadcrumbItem[];
    heading: string;
    description: string;
    posts: PreparedBlogPost[];
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | ConflictingHtmlEvents
>;

export type AllBlogSectionClientProps = BaseProps & SafeSectionHtmlProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const cardFadeIn = {
    hidden: { y: 40, opacity: 0 },
    visible: (index: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: 0.2 + (index % 2) * 0.2 },
    }),
} as const;

const aspectToHeight = (w: number) => Math.round((w * 3) / 4);

type SanityImageProps = {
    image: SanityImageSource;
    alt: string;
    sizes: string;
    className?: string;
    blurDataURL?: string;
    priority?: boolean;
    quality?: number;
};

function SanityImage({
                         image,
                         alt,
                         sizes,
                         className,
                         blurDataURL,
                         priority = false,
                         quality = 75,
                     }: SanityImageProps) {
    const loader: ImageLoader = ({ width, quality: q }) =>
        urlFor(image)
            .width(width)
            .height(aspectToHeight(width))
            .fit("crop")
            .auto("format")
            .quality(q ?? quality)
            .url() ?? "";

    return (
        <Image
            src="/sanity"
            alt={alt}
            loader={loader}
            fill
            sizes={sizes}
            priority={priority}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            draggable={false}
            placeholder={blurDataURL ? "blur" : "empty"}
            blurDataURL={blurDataURL}
            className={className}
        />
    );
}

export default function AllBlogSectionClient({
                                                 breadcrumbs,
                                                 heading,
                                                 description,
                                                 posts,
                                                 className,
                                                 ...rest
                                             }: AllBlogSectionClientProps) {
    return (
        <LazyMotion features={domAnimation} strict>
            <m.section
                id="relume"
                initial="hidden"
                animate="visible"
                className={clsx("px-[5%] py-16 md:py-24 lg:py-36 mt-24 md:mt-12", className)}
                {...rest}
            >
                <div className="container">
                    {/* NAGŁÓWEK */}
                    <div className="mb-12 md:mb-18 lg:mb-36">
                        <div className="mx-auto w-full max-w-lg text-center">
                            {breadcrumbs && breadcrumbs.length > 0 && (
                                <m.div variants={slideUp} custom={0} className="mb-10 md:mb-14 flex justify-center">
                                    <div className="relative inline-block">
                                        <Breadcrumbs items={breadcrumbs} />
                                        <Underline
                                            delay={0.6}
                                            className="pointer-events-none absolute -left-12 top-full mt-2 text-accent h-5 w-[170px] md:h-6 md:w-[230px]"
                                        />
                                    </div>
                                </m.div>
                            )}
                            <m.h1 variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 1 : 0} className="mb-4 text-4xl font-medium md:mb-5 md:text-6xl">
                                {heading}
                            </m.h1>
                            <m.p variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 2 : 1} className="md:text-md mb-6 md:mb-10">
                                {description}
                            </m.p>
                            <m.div variants={slideUp} custom={breadcrumbs && breadcrumbs.length > 0 ? 3 : 2}>
                                <CtaButton />
                            </m.div>
                        </div>
                    </div>

                    {/* LISTA WPISÓW */}
                    <div className="grid grid-cols-1 items-start gap-x-10 gap-y-12 md:grid-cols-2 md:gap-y-16">
                        {posts.map((post, index) => {
                            const sizes = "(min-width: 768px) 50vw, 100vw";
                            const hasSanity = Boolean(post.imageSanity);

                            return (
                                <m.article
                                    key={post.url}
                                    variants={cardFadeIn}
                                    custom={index}
                                    className={[
                                        // ✅ hover WYŁĄCZONY na mobile: wszystko hover/group-hover przeniesione na md:
                                        "group self-start flex flex-col overflow-hidden rounded-[40px] bg-primary-light text-black pb-6 md:pb-8 transition-colors duration-500 md:hover:bg-accent md:hover:text-primary-light",
                                        index % 2 === 1 ? "md:mt-36" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                >
                                    <Link href={post.url} className="block overflow-hidden p-2">
                                        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[32px]">
                                            {hasSanity ? (
                                                <SanityImage
                                                    image={post.imageSanity!}
                                                    alt={post.imageAlt ?? ""}
                                                    sizes={sizes}
                                                    blurDataURL={post.blurDataURL}
                                                    // ✅ skala tylko na md+
                                                    className="object-cover object-top transition-transform duration-700 md:group-hover:scale-105"
                                                />
                                            ) : post.image ? (
                                                <Image
                                                    src={post.image.src}
                                                    alt={post.image.alt ?? ""}
                                                    fill
                                                    sizes={sizes}
                                                    loading="lazy"
                                                    decoding="async"
                                                    draggable={false}
                                                    // ✅ skala tylko na md+
                                                    className="object-cover object-top transition-transform duration-700 md:group-hover:scale-105"
                                                />
                                            ) : null}
                                        </div>
                                    </Link>

                                    <div className="flex flex-1 flex-col px-5 pt-4 md:px-6 md:pt-5">
                                        <div className="mt-auto flex flex-col gap-2">
                                            <Link href={post.url} className="block w-full">
                                                <h2 className="text-base md:text-lg font-medium leading-tight">{post.title}</h2>
                                            </Link>

                                            {/* ✅ przesunięcie tylko na md+ */}
                                            <div className="self-end transition-transform duration-300 md:group-hover:translate-x-1">
                                                <TextLinkButton
                                                    text="czytaj"
                                                    href={post.url}
                                                    primaryColor="currentColor"
                                                    hoverColor="currentColor"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </m.article>
                            );
                        })}
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}